import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, Plus, Trash2, Loader2, Info, ChevronDown, ChevronUp, X, Save, AlertCircle, CheckCircle2, Download
} from "lucide-react";
import { useTaxYear } from "../contexts/TaxYearContext";
import TaxYearBadge from "../components/TaxYearBadge";
import ReportActions from "../components/ReportActions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat("en-US").format(n || 0);

const CATEGORIES = ["All", "Playground Equipment", "Furniture & Fixtures", "Electronics", "Kitchen Equipment", "Vehicle", "Other"];
const INPUT_CATEGORIES = CATEGORIES.filter(c => c !== "All");

const USEFUL_LIFE_REF = [
  { type: "Playground Equipment", years: "10–15 yrs" },
  { type: "Furniture & Fixtures", years: "5–7 yrs" },
  { type: "Kitchen Equipment", years: "7–10 yrs" },
  { type: "Electronics / Computers", years: "3–5 yrs" },
  { type: "Vehicles", years: "5 yrs (MACRS Listed Property)" },
  { type: "Buildings", years: "27.5–39 yrs (consult accountant)" },
];

const KEYWORD_MAP = [
  { keywords: ["playground", "swing", "slide", "climbing", "outdoor", "jungle"], category: "Playground Equipment" },
  { keywords: ["stove", "oven", "refrigerator", "freezer", "microwave", "dishwasher", "kitchen"], category: "Kitchen Equipment" },
  { keywords: ["computer", "laptop", "desktop", "tablet", "printer", "monitor", "tv", "television", "camera", "projector", "ipad"], category: "Electronics" },
  { keywords: ["table", "chair", "desk", "sofa", "furniture", "cot", "bed", "shelf", "bookcase", "cabinet"], category: "Furniture & Fixtures" },
  { keywords: ["vehicle", "van", "suv", "truck", "auto", "car", "minivan", "shuttle", "bus", "sienna", "transit", "odyssey"], category: "Vehicle" },
];

function suggestCategory(name) {
  const lower = (name || "").toLowerCase();
  for (const { keywords, category } of KEYWORD_MAP) {
    if (keywords.some(k => lower.includes(k))) return category;
  }
  return "";
}

const EMPTY_FORM = {
  name: "", category: "", purchase_date: "", cost: "", useful_life_years: "", notes: "",
  // Vehicle fields (only sent when category === "Vehicle")
  vehicle_gvwr_class: "", business_use_percentage: "", total_miles: "",
  business_miles: "", commuting_miles: "", deduction_method: "",
  vehicle_in_service_date: "", has_written_evidence: false,
};

// Notify the sidebar (and any future subscribers) that equipment data has changed.
// Used by the "Drafts (N)" badge in Layout.js.
const emitEquipmentChanged = () => {
  try { window.dispatchEvent(new CustomEvent("equipment:changed")); } catch {/* no-op */}
};

export default function EquipmentAssets() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const { taxYear } = useTaxYear();
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [confirmingDraftId, setConfirmingDraftId] = useState(null);
  const [draftEdits, setDraftEdits] = useState({}); // {id: {category, useful_life_years}}
  // Item 3 — Section 179 page-level summary
  const [s179Summary, setS179Summary] = useState(null);
  // Vehicles backlog — Listed Property summary
  const [vehicleSummary, setVehicleSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const [itemsRes, sumRes, s179Res, vehRes] = await Promise.all([
        axios.get(`${API}/equipment?tax_year=${taxYear}`, { withCredentials: true }),
        axios.get(`${API}/equipment/depreciation-summary`, { withCredentials: true }),
        axios.get(`${API}/equipment/section-179-summary?tax_year=${taxYear}`, { withCredentials: true }),
        axios.get(`${API}/equipment/vehicle-summary?tax_year=${taxYear}`, { withCredentials: true }),
      ]);
      setItems(itemsRes.data.items || []);
      setNeedsReviewCount(itemsRes.data.needs_review_count || 0);
      setSummary(sumRes.data);
      setS179Summary(s179Res.data);
      setVehicleSummary(vehRes.data);
    } catch {
      toast.error("Could not load equipment");
    } finally {
      setLoading(false);
    }
  }, [taxYear]);

  useEffect(() => { load(); }, [load]);

  const handleNameChange = (name) => {
    const suggested = suggestCategory(name);
    setForm(f => ({
      ...f,
      name,
      category: f.category || suggested,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.purchase_date || !form.cost || !form.useful_life_years) {
      toast.error("Please fill in all required fields");
      return;
    }
    // Vehicle-specific required fields
    if (form.category === "Vehicle") {
      if (!form.deduction_method) {
        toast.error("Pick a deduction method (Standard Mileage or Actual Expenses)");
        return;
      }
      if (!form.business_use_percentage) {
        toast.error("Business use % is required for vehicles");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        purchase_date: form.purchase_date,
        cost: parseFloat(form.cost),
        useful_life_years: parseInt(form.useful_life_years, 10),
        notes: form.notes,
      };
      if (form.category === "Vehicle") {
        payload.vehicle_gvwr_class = form.vehicle_gvwr_class || null;
        payload.business_use_percentage = parseFloat(form.business_use_percentage) || 0;
        payload.total_miles = parseInt(form.total_miles || 0, 10);
        payload.business_miles = parseInt(form.business_miles || 0, 10);
        payload.commuting_miles = parseInt(form.commuting_miles || 0, 10);
        payload.deduction_method = form.deduction_method;
        payload.vehicle_in_service_date = form.vehicle_in_service_date || form.purchase_date;
        payload.has_written_evidence = !!form.has_written_evidence;
      }
      await axios.post(`${API}/equipment`, payload, { withCredentials: true });
      toast.success("Equipment added");
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
      emitEquipmentChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add equipment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/equipment/${id}`, { withCredentials: true });
      toast.success("Equipment removed");
      setConfirmDelete(null);
      await load();
      emitEquipmentChanged();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const handleConfirmDraft = async (id) => {
    const edits = draftEdits[id] || {};
    const item = items.find(i => i.id === id);
    if (!item) return;
    const category = edits.category ?? item.category;
    const life = parseInt(edits.useful_life_years ?? item.useful_life_years, 10);
    if (!category || !INPUT_CATEGORIES.includes(category)) {
      toast.error("Pick a category before confirming");
      return;
    }
    if (!life || life <= 0) {
      toast.error("Useful life must be greater than 0");
      return;
    }
    setConfirmingDraftId(id);
    try {
      await axios.patch(`${API}/equipment/${id}`, {
        category,
        useful_life_years: life,
        status: "confirmed",
      }, { withCredentials: true });
      toast.success("Equipment confirmed — depreciation now flows to your accountant");
      setDraftEdits(d => { const next = { ...d }; delete next[id]; return next; });
      await load();
      emitEquipmentChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Confirm failed");
    } finally {
      setConfirmingDraftId(null);
    }
  };

  const filtered = activeTab === "All" ? items : items.filter(i => i.category === activeTab);
  const confirmedItems = items.filter(i => (i.status || "confirmed") === "confirmed");
  const thisYear = taxYear;
  // Totals reflect only confirmed items (drafts contribute zero depreciation until confirmed)
  const totalCost = confirmedItems.reduce((s, i) => s + (i.cost || 0), 0);
  const totalAccum = confirmedItems.reduce((s, i) => s + (i.accumulated_depreciation || 0), 0);
  const totalNBV = confirmedItems.reduce((s, i) => s + (i.net_book_value || 0), 0);
  const totalThisYear = confirmedItems.reduce((s, i) => s + (i.this_year_deduction || 0), 0);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}>
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Equipment & Assets</h1>
            <TaxYearBadge className="ml-2" />
            <p className="text-sm text-slate-500">Track equipment and calculate depreciation for your accountant</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportActions
            taxYear={taxYear}
            category="Equipment & Assets"
            source="/api/reports/equipment-pdf"
            sourceParams={{}}
            filenameHint={`equipment-${taxYear}.pdf`}
            testId="equipment"
          />
          <button
            data-testid="add-equipment-btn"
            onClick={() => setShowForm(s => !s)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all"
            style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Add Equipment"}
          </button>
        </div>
      </div>

      {/* Drafts Awaiting Confirmation Banner */}
      {/* Section 179 page-level warnings */}
      {s179Summary && s179Summary.exceeds_income && s179Summary.total_179_elected > 0 && (
        <div className="flex items-start gap-3 bg-red-50 border-2 border-red-300 rounded-xl p-4" data-testid="s179-exceeds-income-banner">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-900" style={{ fontFamily: "Manrope" }}>
              Your Section 179 elections exceed estimated taxable income
            </p>
            <p className="text-xs text-red-800 mt-1 leading-relaxed">
              You've elected <strong>{fmt(s179Summary.total_179_elected)}</strong> in Section 179 deductions, but your estimated {taxYear} taxable income is <strong>{fmt(s179Summary.taxable_income_estimate)}</strong>. Section 179 cannot create a net loss. Reduce one or more elections (partial 179) or consult your accountant about carry-forward options.
            </p>
          </div>
        </div>
      )}
      {s179Summary && s179Summary.exceeds_irs_limit && (
        <div className="flex items-start gap-3 bg-red-50 border-2 border-red-300 rounded-xl p-4" data-testid="s179-exceeds-limit-banner">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-800">
            Your total Section 179 elections exceed the {taxYear} IRS annual limit of {fmt(s179Summary.annual_irs_limit)}. Reduce or consult your accountant.
          </p>
        </div>
      )}

      {needsReviewCount > 0 && (
        <div
          data-testid="equip-drafts-banner"
          className="flex items-start gap-3 bg-amber-50 border-2 border-amber-300 rounded-xl p-4"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900" style={{ fontFamily: "Manrope" }}>
              {needsReviewCount} item{needsReviewCount === 1 ? "" : "s"} from uploaded receipts need your review
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              We auto-created these drafts when you uploaded equipment receipts. <strong>Confirm the category &amp; useful life</strong> so depreciation flows to your accountant for Schedule C Line 13. Drafts contribute $0 to your deduction until confirmed.
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Equipment Cost", value: fmt(totalCost), sub: `${items.length} items`, testid: "equip-total-cost" },
          { label: "Accumulated Depreciation", value: fmt(totalAccum), sub: "All time", testid: "equip-accum" },
          { label: "Net Book Value", value: fmt(totalNBV), sub: `As of Dec 31, ${thisYear}`, testid: "equip-nbv" },
          { label: `${thisYear} Depreciation`, value: fmt(totalThisYear), sub: "This year's deduction", testid: "equip-this-year", highlight: true },
        ].map(c => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={c.testid}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{c.label}</p>
            <p className={`text-xl font-bold mt-1 ${c.highlight ? "text-violet-700" : "text-slate-900"}`} style={{ fontFamily: "Manrope" }}>{c.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Section 179 KPI tile (Item 3) */}
      {s179Summary && s179Summary.total_179_elected > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="s179-kpi-tile">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">{taxYear} Section 179 Elected</p>
              <p className="text-2xl font-extrabold text-amber-900" style={{ fontFamily: "Manrope" }}>{fmt(s179Summary.total_179_elected)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Estimated Tax Savings</p>
              <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: "Manrope" }}>{fmt(s179Summary.estimated_tax_savings)}</p>
              <p className="text-[10px] text-slate-500 italic">Assumes {Math.round((s179Summary.blended_bracket_used || 0.22) * 100)}% blended bracket — consult your accountant</p>
            </div>
            <div className="border-l border-amber-200 pl-3">
              <ReportActions
                taxYear={taxYear}
                category="Form 4562 Part I"
                source="/api/equipment/form-4562-pdf"
                sourceParams={{}}
                filenameHint={`form-4562-part1-${taxYear}.pdf`}
                testId="form-4562"
              />
            </div>
          </div>
        </div>
      )}

      {/* Vehicles (Listed Property) summary tile — Form 4562 Part V */}
      {vehicleSummary && vehicleSummary.totals && vehicleSummary.totals.vehicle_count > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4" data-testid="vehicles-summary-tile">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700">{taxYear} Vehicles (Listed Property)</p>
                <p className="text-lg font-extrabold text-blue-900" style={{ fontFamily: "Manrope" }}>
                  {vehicleSummary.totals.vehicle_count} vehicle{vehicleSummary.totals.vehicle_count === 1 ? "" : "s"} tracked
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Standard Mileage Total</p>
                <p className="text-base font-bold text-emerald-700">{fmt(vehicleSummary.totals.standard_mileage_total)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Actual Deprec. Total</p>
                <p className="text-base font-bold text-violet-700">{fmt(vehicleSummary.totals.actual_depreciation_total)}</p>
              </div>
              <div className="border-l border-blue-200 pl-3">
                <ReportActions
                  taxYear={taxYear}
                  category="Form 4562 Part V"
                  source="/api/equipment/form-4562-part-v-pdf"
                  sourceParams={{}}
                  filenameHint={`form-4562-part-v-${taxYear}.pdf`}
                  testId="form-4562-part-v"
                />
              </div>
            </div>
          </div>
          {vehicleSummary.warnings && vehicleSummary.warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mt-2" data-testid="vehicles-cap-warnings">
              <p className="text-xs font-bold text-amber-900 mb-1">IRS §280F Luxury Auto Cap Warnings</p>
              {vehicleSummary.warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-800 leading-relaxed">
                  <strong>{w.vehicle}:</strong> {w.cap_warning}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Accountant Note */}
      <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-xl p-3">
        <Info className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-violet-800">
          <strong>Your {thisYear} depreciation total is {fmt(totalThisYear)}.</strong> Share this with your accountant for Schedule C Line 13 (Depreciation and Section 179). All values use Straight Line method. Consult your accountant for Section 179 or bonus depreciation options.
        </p>
      </div>

      {/* Add Equipment Form */}
      {showForm && (
        <div className="bg-white border border-blue-200 rounded-2xl shadow-sm p-6">
          <h2 className="font-bold text-slate-900 text-base mb-4" style={{ fontFamily: "Manrope" }}>Add Equipment</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Item Name *</label>
              <input
                data-testid="equip-form-name"
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
                placeholder="e.g. Playground Structure, Commercial Refrigerator"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Category *</label>
              <select
                data-testid="equip-form-category"
                value={form.category}
                onChange={e => {
                  const cat = e.target.value;
                  setForm(f => ({
                    ...f,
                    category: cat,
                    // Auto-fill useful life when switching to Vehicle (IRS 5-yr MACRS)
                    useful_life_years: cat === "Vehicle" && !f.useful_life_years ? "5" : f.useful_life_years,
                  }));
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">Select category…</option>
                {INPUT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {form.name && suggestCategory(form.name) && (
                <p className="text-xs text-blue-600 mt-1">Suggested: {suggestCategory(form.name)}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Purchase Date *</label>
              <input
                data-testid="equip-form-date"
                type="date"
                value={form.purchase_date}
                onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Cost ($) *</label>
              <input
                data-testid="equip-form-cost"
                type="number"
                min="0.01"
                step="0.01"
                value={form.cost}
                onChange={e => setForm(f => ({ ...f, cost: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">Useful Life (years) *</label>
                <div className="relative group">
                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 w-64 bg-slate-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                    <p className="font-semibold mb-1.5">IRS Typical Useful Life</p>
                    {USEFUL_LIFE_REF.map(r => (
                      <div key={r.type} className="flex justify-between gap-3"><span className="text-slate-300">{r.type}</span><span className="text-white font-medium whitespace-nowrap">{r.years}</span></div>
                    ))}
                    <p className="mt-2 text-slate-400">These are estimates. Consult your accountant.</p>
                  </div>
                </div>
              </div>
              <input
                data-testid="equip-form-life"
                type="number"
                min="1"
                max="50"
                value={form.useful_life_years}
                onChange={e => setForm(f => ({ ...f, useful_life_years: e.target.value }))}
                placeholder="Enter years"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Notes</label>
              <input
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional — vendor, serial number, location"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            {/* Vehicle-specific fields — IRS Form 4562 Part V (Listed Property) */}
            {form.category === "Vehicle" && (
              <div className="md:col-span-2 bg-blue-50/60 border border-blue-200 rounded-xl p-4 space-y-3" data-testid="vehicle-fields-panel">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-bold text-blue-900" style={{ fontFamily: "Manrope" }}>
                    Vehicle Details — Form 4562 Part V (Listed Property)
                  </h3>
                </div>
                <p className="text-xs text-blue-800 leading-relaxed">
                  Vehicles are <strong>Listed Property under IRC §280F</strong>. Section 179 requires &gt;50% business use AND contemporaneous written records (mileage log). Pick standard mileage OR actual expenses — you can't mix the two in the same year.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">Deduction Method *</label>
                    <select
                      data-testid="veh-form-method"
                      value={form.deduction_method}
                      onChange={e => setForm(f => ({ ...f, deduction_method: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Pick method…</option>
                      <option value="standard_mileage">Standard Mileage ($0.70/mi 2025)</option>
                      <option value="actual_expenses">Actual Expenses (depreciation)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">GVWR Class</label>
                    <select
                      data-testid="veh-form-gvwr"
                      value={form.vehicle_gvwr_class}
                      onChange={e => setForm(f => ({ ...f, vehicle_gvwr_class: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Pick GVWR class…</option>
                      <option value="car">Passenger car (≤6,000 lbs)</option>
                      <option value="suv_truck_van">SUV / truck / van (&gt;6,000 lbs)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">Business Use % *</label>
                    <input
                      data-testid="veh-form-bus-use"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.business_use_percentage}
                      onChange={e => setForm(f => ({ ...f, business_use_percentage: e.target.value }))}
                      placeholder="e.g. 75"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">In-Service Date</label>
                    <input
                      type="date"
                      value={form.vehicle_in_service_date}
                      onChange={e => setForm(f => ({ ...f, vehicle_in_service_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">Total Miles ({taxYear})</label>
                    <input
                      data-testid="veh-form-total-miles"
                      type="number"
                      min="0"
                      value={form.total_miles}
                      onChange={e => setForm(f => ({ ...f, total_miles: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">Business Miles</label>
                    <input
                      data-testid="veh-form-bus-miles"
                      type="number"
                      min="0"
                      value={form.business_miles}
                      onChange={e => setForm(f => ({ ...f, business_miles: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wider">Commuting Miles</label>
                    <input
                      data-testid="veh-form-com-miles"
                      type="number"
                      min="0"
                      value={form.commuting_miles}
                      onChange={e => setForm(f => ({ ...f, commuting_miles: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs">
                      <input
                        data-testid="veh-form-written"
                        type="checkbox"
                        checked={form.has_written_evidence}
                        onChange={e => setForm(f => ({ ...f, has_written_evidence: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-slate-700 font-medium">I keep a written mileage log (IRS §1.274-5T)</span>
                    </label>
                  </div>
                </div>
                {form.business_miles && (
                  <p className="text-xs text-emerald-700 italic">
                    Est. standard mileage deduction: <strong>{fmt(parseInt(form.business_miles || 0, 10) * 0.70)}</strong> (at $0.70/mi)
                  </p>
                )}
              </div>
            )}
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                data-testid="equip-form-submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #2563EB, #7C3AED)" }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Add Equipment"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category Tabs + Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Tabs */}
        <div className="px-4 pt-4 border-b border-slate-100 flex gap-1 overflow-x-auto">
          {CATEGORIES.map(c => (
            <button
              key={c}
              data-testid={`equip-tab-${c.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setActiveTab(c)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-t-lg whitespace-nowrap transition-all ${
                activeTab === c
                  ? "bg-violet-600 text-white"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              }`}
            >
              {c}
              {c !== "All" && <span className="ml-1 opacity-70">{items.filter(i => i.category === c).length}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-violet-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-slate-500">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-500">No equipment in this category</p>
            <p className="text-xs text-slate-400 mt-1">Upload an equipment receipt or add manually above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="equipment-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Item Name", "Category", "Purchase Date", "Cost", "Life (yrs)", "Annual Deduction", "Accumulated Deprec.", "Net Book Value"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const isDraft = (item.status || "confirmed") === "needs_review";
                  const edits = draftEdits[item.id] || {};
                  const draftCat = edits.category ?? item.category;
                  const draftLife = edits.useful_life_years ?? item.useful_life_years;
                  return (
                  <Fragment key={item.id}>
                  <tr
                    data-testid={isDraft ? `equip-draft-row-${item.id}` : `equip-row-${item.id}`}
                    className={`border-b border-slate-50 transition-colors ${isDraft ? "bg-amber-50/40 hover:bg-amber-50" : "hover:bg-slate-50"}`}
                  >
                    <td className="px-4 py-3 font-medium text-slate-800 max-w-[160px] truncate">
                      <div className="flex items-center gap-2">
                        {isDraft && (
                          <span className="bg-amber-200 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
                            Needs Review
                          </span>
                        )}
                        <span className="truncate">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {isDraft ? (
                        <select
                          data-testid={`draft-category-${item.id}`}
                          value={draftCat || ""}
                          onChange={e => setDraftEdits(d => ({ ...d, [item.id]: { ...d[item.id], category: e.target.value } }))}
                          className="text-xs border border-amber-300 rounded px-2 py-1 bg-white focus:outline-none focus:border-amber-500"
                        >
                          <option value="">Pick…</option>
                          {INPUT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : (
                        <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{item.category}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{item.purchase_date}</td>
                    <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">{fmt(item.cost)}</td>
                    <td className="px-4 py-3 text-slate-600 text-center">
                      {isDraft ? (
                        <input
                          data-testid={`draft-life-${item.id}`}
                          type="number"
                          min="1"
                          max="50"
                          value={draftLife || ""}
                          onChange={e => setDraftEdits(d => ({ ...d, [item.id]: { ...d[item.id], useful_life_years: e.target.value } }))}
                          className="w-16 text-center text-xs border border-amber-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:border-amber-500"
                        />
                      ) : item.useful_life_years}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {isDraft ? <span className="text-xs text-slate-400 italic">— after review —</span> : fmt(item.annual_deduction)}
                    </td>
                    <td className="px-4 py-3 text-rose-600 font-medium whitespace-nowrap">
                      {isDraft ? <span className="text-xs text-slate-400 italic">—</span> : fmt(item.accumulated_depreciation)}
                    </td>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap" style={{ color: item.fully_depreciated ? "#94a3b8" : "#1e293b" }}>
                      {isDraft
                        ? <span className="text-xs text-slate-400 italic">—</span>
                        : item.fully_depreciated
                          ? <span className="text-xs text-slate-400">Fully depreciated</span>
                          : fmt(item.net_book_value)}
                    </td>
                    <td className="px-4 py-3">
                      {isDraft ? (
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`confirm-draft-${item.id}`}
                            onClick={() => handleConfirmDraft(item.id)}
                            disabled={confirmingDraftId === item.id}
                            className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white px-2.5 py-1 rounded font-semibold flex items-center gap-1"
                          >
                            {confirmingDraftId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Confirm
                          </button>
                          <button
                            data-testid={`delete-draft-${item.id}`}
                            onClick={() => setConfirmDelete(item.id)}
                            className="text-slate-400 hover:text-red-500 transition-colors ml-1"
                            title="Discard this draft"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : confirmDelete === item.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDelete(item.id)} disabled={deleting === item.id} className="text-xs bg-red-600 text-white px-2 py-1 rounded font-semibold hover:bg-red-700">
                            {deleting === item.id ? "…" : "Delete"}
                          </button>
                          <button onClick={() => setConfirmDelete(null)} className="text-xs text-slate-500 hover:text-slate-700 px-1">Cancel</button>
                        </div>
                      ) : (
                        <button
                          data-testid={`delete-equip-${item.id}`}
                          onClick={() => setConfirmDelete(item.id)}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Item 3 — Section 179 row (confirmed items only) */}
                  {!isDraft && (
                    <tr key={item.id + "-s179"} data-testid={`s179-row-${item.id}`} className="bg-amber-50/30">
                      <td colSpan={8} className="px-4 py-2 text-xs">
                        <div className="flex items-center gap-4 flex-wrap">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              data-testid={`s179-toggle-${item.id}`}
                              checked={!!item.section_179_elected}
                              onChange={async (e) => {
                                const elected = e.target.checked;
                                try {
                                  await axios.patch(`${API}/equipment/${item.id}`,
                                    { section_179_elected: elected, ...(elected ? { section_179_amount: item.cost } : {}) },
                                    { withCredentials: true });
                                  await load();
                                  toast.success(elected ? "Section 179 elected" : "Section 179 unelected");
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Failed to update");
                                }
                              }}
                              className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                            />
                            <span className="font-semibold text-slate-700">Take Section 179 election</span>
                            <span
                              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help"
                              title="Section 179 lets you deduct the FULL purchase price this year instead of spreading it over the asset's useful life. Best for big-ticket items (playgrounds, vehicles, kitchen equipment) when your business has enough taxable income to absorb the deduction. Consult your accountant — there are annual limits."
                            >?</span>
                          </label>
                          {item.section_179_elected && (
                            <>
                              <label className="inline-flex items-center gap-1.5">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Amount:</span>
                                <input
                                  type="number"
                                  data-testid={`s179-amount-${item.id}`}
                                  defaultValue={item.section_179_amount || item.cost}
                                  min={0}
                                  max={item.cost}
                                  step="0.01"
                                  onBlur={async (e) => {
                                    const amt = Number(e.target.value);
                                    if (amt === item.section_179_amount) return;
                                    if (amt < 0 || amt > item.cost) {
                                      toast.error(`Amount must be between $0 and ${fmt(item.cost)}`);
                                      e.target.value = item.section_179_amount || item.cost;
                                      return;
                                    }
                                    try {
                                      await axios.patch(`${API}/equipment/${item.id}`,
                                        { section_179_amount: amt },
                                        { withCredentials: true });
                                      await load();
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "Failed to update");
                                    }
                                  }}
                                  className="w-28 border border-amber-300 rounded px-2 py-1 text-xs"
                                />
                              </label>
                              <span className="text-emerald-700 font-semibold italic">
                                Est. {taxYear} tax savings: {fmt((item.section_179_amount || 0) * 0.22)}
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Depreciation Schedule Toggle */}
      {summary && items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <button
            data-testid="toggle-depreciation-schedule"
            onClick={() => setShowSchedule(s => !s)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Depreciation Schedule ({thisYear})</h2>
            {showSchedule ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {showSchedule && (
            <div className="border-t border-slate-100 overflow-x-auto">
              <table className="w-full text-sm" data-testid="depreciation-schedule-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["Category", "Items", "Total Cost", "Prior Depreciation", `${thisYear} Deduction`, "Accumulated", "Net Book Value"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {INPUT_CATEGORIES.filter(cat => (summary.by_category[cat]?.count || 0) > 0).map(cat => {
                    const r = summary.by_category[cat];
                    return (
                      <tr key={cat} className="border-b border-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-700">{cat}</td>
                        <td className="px-4 py-3 text-slate-600 text-center">{r.count}</td>
                        <td className="px-4 py-3 text-slate-800 font-medium">{fmt(r.total_cost)}</td>
                        <td className="px-4 py-3 text-slate-600">{fmt(r.prior_depreciation)}</td>
                        <td className="px-4 py-3 text-violet-700 font-semibold">{fmt(r.this_year)}</td>
                        <td className="px-4 py-3 text-rose-600">{fmt(r.accumulated)}</td>
                        <td className="px-4 py-3 text-slate-800 font-semibold">{fmt(r.net_book_value)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-slate-50 font-bold border-t-2 border-slate-200">
                    <td className="px-4 py-3 text-slate-900">Total</td>
                    <td className="px-4 py-3 text-slate-900 text-center">{summary.totals.count}</td>
                    <td className="px-4 py-3 text-slate-900">{fmt(summary.totals.total_cost)}</td>
                    <td className="px-4 py-3 text-slate-900">{fmt(summary.totals.prior_depreciation)}</td>
                    <td className="px-4 py-3 text-violet-700">{fmt(summary.totals.this_year)}</td>
                    <td className="px-4 py-3 text-rose-600">{fmt(summary.totals.accumulated)}</td>
                    <td className="px-4 py-3 text-slate-900">{fmt(summary.totals.net_book_value)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="px-6 py-3 text-xs text-slate-400 border-t border-slate-100">
                Depreciation method: Straight Line (SL) · Salvage value: $0 · All values are estimates. Please consult your accountant for tax advice.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
